import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import adminService from '../../services/adminService';
import PageHeader from '../../components/common/PageHeader';
import { API_BASE_URL } from '../../constants/config';

const BankDetailsManagement = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        orgNameEn: '',
        orgNameNe: '',
        campaignName: '',
        bankName: '',
        accountNumber: '',
        accountName: '',
        branch: ''
    });
    const [currentQrCode, setCurrentQrCode] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchBankSettings();
    }, []);

    const normalizeUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('data:') || url.startsWith('http')) return url;
        const baseUrl = API_BASE_URL.split('/main/api/v1')[0];
        return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const fetchBankSettings = async () => {
        try {
            setFetching(true);
            const response = await adminService.getBankSettings();
            const data = response?.data || response;

            if (data) {
                setFormData({
                    orgNameEn: data.orgNameEn || '',
                    orgNameNe: data.orgNameNe || '',
                    campaignName: data.campaignName || '',
                    bankName: data.bankName || '',
                    accountNumber: data.accountNumber || '',
                    accountName: data.accountName || '',
                    branch: data.branch || ''
                });

                if (data.qrCodeUrl) {
                    setCurrentQrCode(normalizeUrl(data.qrCodeUrl));
                }
            }
        } catch (error) {
            console.error('Error fetching bank settings:', error);
            // Initialize with default values if fetch fails (e.g., API not ready yet)
            setFormData({
                orgNameEn: 'Vaidik Anusandhan Guthi',
                orgNameNe: 'वैदिक अनुसन्धान गुठी',
                campaignName: 'दान सेवा अभियान',
                bankName: 'Nabil Bank Ltd.',
                accountNumber: '20601017500292',
                accountName: 'Vaidik Anusandhan Guthi',
                branch: 'Mitrapark Branch, Kathmandu'
            });
        } finally {
            setFetching(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'File size too large. Max 2MB allowed.' });
                return;
            }
            setSelectedFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setMessage({ type: '', text: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setMessage({ type: '', text: '' });

            // 1. Update text fields
            await adminService.updateBankSettings(formData);

            // 2. Upload QR code if selected
            if (selectedFile) {
                const uploadData = new FormData();
                uploadData.append('file', selectedFile);
                const uploadResponse = await adminService.updateQrCode(uploadData);
                const newQrUrl = uploadResponse?.data?.qrCodeUrl || uploadResponse?.qrCodeUrl;
                if (newQrUrl) {
                    setCurrentQrCode(normalizeUrl(newQrUrl));
                }
                setSelectedFile(null);
                setPreviewUrl(null);
            }

            setMessage({ type: 'success', text: 'Bank details updated successfully!' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update bank details.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-primary-50 pb-12">
            <PageHeader
                title="Bank Details Management"
                subtitle="Update bank account info and payment QR code for the donation modal"
                icon={
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                }
            />

            <div className="max-w-4xl mx-auto px-4 mt-8">
                <div className="bg-white rounded-2xl shadow-sm border border-primary-100 overflow-hidden">
                    <form onSubmit={handleSubmit} className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="md:col-span-2">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Organization & Campaign</h3>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Organization Name (English)</label>
                                <input
                                    type="text"
                                    name="orgNameEn"
                                    value={formData.orgNameEn}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Organization Name (Nepali)</label>
                                <input
                                    type="text"
                                    name="orgNameNe"
                                    value={formData.orgNameNe}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name (Nepali)</label>
                                <input
                                    type="text"
                                    name="campaignName"
                                    value={formData.campaignName}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2 mt-4">Bank Account Details</h3>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                                <input
                                    type="text"
                                    name="bankName"
                                    value={formData.bankName}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                                <input
                                    type="text"
                                    name="accountNumber"
                                    value={formData.accountNumber}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                                <input
                                    type="text"
                                    name="accountName"
                                    value={formData.accountName}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                                <input
                                    type="text"
                                    name="branch"
                                    value={formData.branch}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="mb-8">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Payment QR Code</h3>
                            <div className="flex flex-col md:flex-row gap-8 items-start">
                                <div className="w-full md:w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-4">Current QR Code</label>
                                    <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden border border-gray-200 flex items-center justify-center">
                                        {fetching ? (
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                        ) : currentQrCode ? (
                                            <img src={currentQrCode} alt="Current QR Code" className="w-full h-full object-contain" />
                                        ) : (
                                            <div className="text-gray-400 text-center p-4">
                                                <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 17h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                                </svg>
                                                <p className="text-xs">No QR code uploaded</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full md:w-1/2">
                                    <label className="block text-sm font-medium text-gray-700 mb-4">Upload New QR Code</label>
                                    <div className={`flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-xl cursor-pointer transition duration-150 ${previewUrl ? 'border-primary-300 bg-primary-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}>
                                        <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                                            {previewUrl ? (
                                                <img src={previewUrl} alt="Preview" className="w-full h-full object-contain p-4" />
                                            ) : (
                                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                    <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                    </svg>
                                                    <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span></p>
                                                    <p className="text-xs text-gray-400">PNG or JPG (MAX. 2MB)</p>
                                                </div>
                                            )}
                                            <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                        </label>
                                    </div>
                                    {previewUrl && (
                                        <button
                                            type="button"
                                            onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                                            className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Remove Preview
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {message.text && (
                            <div className={`p-4 rounded-xl mb-6 flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                {message.type === 'success' ? (
                                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                )}
                                {message.text}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-6 border-t">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition"
                            >
                                Back to Dashboard
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-10 py-2.5 rounded-xl font-bold transition flex items-center ${loading
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary-600 text-white hover:bg-primary-700 shadow-md hover:shadow-lg'
                                    }`}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Updating...
                                    </>
                                ) : 'Save Bank Details'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BankDetailsManagement;
